import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { listCompanyUsers, getCalendar, getTimesheet, setTimesheetEntry, exportTimesheet, listFiredForPeriod, listLocations } from "../api";
import { ROLES, statusesAsList, statusesAsMap, entryKey } from "../constants";
import { daysInMonth, weekdayMon0, ddKey, todayParts } from "../utils/dates";
import LocationFilter, { applyLocationFilter } from "../components/LocationFilter";
import AppPage from "../components/AppPage";

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const WD_KEYS = ["mon","tue","wed","thu","fri","sat","sun"];

export default function Timesheet() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const today = todayParts();
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [users, setUsers] = useState([]);
  const [firedUsers, setFiredUsers] = useState([]);
  const [showFired, setShowFired] = useState(false);
  const [calendar, setCalendar] = useState({ days: {} });
  const [timesheet, setTimesheet] = useState({ entries: {} });
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [err, setErr] = useState("");
  const [compact, setCompact] = useState(true);
  const [exporting, setExporting] = useState(false);

  const statuses = useMemo(() => statusesAsList(company), [company]);
  const statusMap = useMemo(() => statusesAsMap(statuses), [statuses]);

  const reload = async () => {
    const [u, cal, ts, locs] = await Promise.all([
      listCompanyUsers(),
      getCalendar(year, month),
      getTimesheet(year, month),
      listLocations().catch(() => []),
    ]);
    setUsers(u); setCalendar(cal); setTimesheet(ts); setLocations(locs || []);
    // Fired users for period — only for privileged users (director/hr)
    if (user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role)) {
      try {
        const fired = await listFiredForPeriod(year, month);
        setFiredUsers(fired || []);
      } catch { setFiredUsers([]); }
    } else {
      setFiredUsers([]);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year, month]);

  const isPrivileged = user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role);
  const isManager = user?.role === ROLES.MANAGER;

  const visibleUsers = useMemo(() => {
    if (!user) return [];
    let base;
    if (isPrivileged) base = users;
    else if (isManager) base = users.filter(u => u.manager_id === user.user_id || u.user_id === user.user_id);
    else base = users.filter(u => u.user_id === user.user_id);
    return applyLocationFilter(base, locationFilter);
  }, [users, user, isPrivileged, isManager, locationFilter]);

  const total = daysInMonth(year, month);

  const canEdit = (day) => {
    if (isPrivileged) return true;
    if (!isManager) return false;
    const now = new Date();
    if (now.getFullYear() !== year || now.getMonth() !== month) return false;
    return day === now.getDate();
  };

  const onCellChange = async (uid, day, status) => {
    setErr("");
    if (!status) return;
    try {
      await setTimesheetEntry(year, month, uid, day, status);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || t("timesheet.save_error"));
    }
  };

  const dayHeaders = [];
  const todayNow = new Date();
  const isCurrentMonth = todayNow.getFullYear() === year && todayNow.getMonth() === month;
  const todayDay = isCurrentMonth ? todayNow.getDate() : null;
  for (let d = 1; d <= total; d++) {
    const wd = weekdayMon0(year, month, d);
    const calStatus = calendar.days?.[ddKey(d)] || (wd >= 5 ? "weekend" : "work");
    dayHeaders.push({ d, wd, calStatus, isToday: d === todayDay });
  }

  return (
    <AppPage
      testId="timesheet-page"
      bodyScroll="table"
      className="ts-scroll"
      toolbar={(
        <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">{t("timesheet.title")}</h4>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <Link to="/vacation-schedule" className="btn btn-outline-info btn-sm text-nowrap"
                data-testid="ts-vacation-schedule">
            <i className="bi bi-sun me-1"></i>{t("nav.vacation_schedule")}
          </Link>
          {isPrivileged && (
            <Link to="/calendar" className="btn btn-outline-primary btn-sm text-nowrap"
                  data-testid="ts-production-calendar">
              <i className="bi bi-calendar3 me-1"></i>{t("nav.calendar")}
            </Link>
          )}
          {isPrivileged && (
            <LocationFilter locations={locations} users={users}
                            value={locationFilter} onChange={setLocationFilter}
                            testid="ts-location-filter" />
          )}
          <select className="form-select" style={{ width: 150 }} value={month}
                  onChange={(e) => setMonth(Number(e.target.value))} data-testid="ts-month">
            {MONTH_KEYS.map((mk, i) => <option key={i} value={i}>{t(`months.${mk}`)}</option>)}
          </select>
          <select className="form-select" style={{ width: 100 }} value={year}
                  onChange={(e) => setYear(Number(e.target.value))} data-testid="ts-year">
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="btn-group" role="group" aria-label={t("timesheet.export_title")}>
            <button className="btn btn-outline-success" disabled={exporting}
                    onClick={async () => { setExporting(true); try { await exportTimesheet(year, month, "xlsx", locationFilter); } finally { setExporting(false); } }}
                    data-testid="ts-export-xlsx" title={t("timesheet.export_excel_title")}>
              <i className="bi bi-file-earmark-excel me-1"></i>Excel
            </button>
            <button className="btn btn-outline-danger" disabled={exporting}
                    onClick={async () => { setExporting(true); try { await exportTimesheet(year, month, "pdf", locationFilter); } finally { setExporting(false); } }}
                    data-testid="ts-export-pdf" title={t("timesheet.export_pdf_title")}>
              <i className="bi bi-file-earmark-pdf me-1"></i>PDF
            </button>
          </div>
          <button className={`btn ${compact ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setCompact(!compact)} title={t("timesheet.compact_title")}
                  data-testid="ts-compact-toggle">
            <i className="bi bi-arrows-angle-contract"></i>
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}

      <div className="d-flex flex-wrap gap-2 mb-3 small">
        {statuses.map(s => (
          <span key={s.key} className="d-inline-flex align-items-center gap-1">
            <span className="status-pill" style={{ background: s.color }}>{s.short}</span>
            <span className="text-muted">{s.label}</span>
          </span>
        ))}
      </div>
        </>
      )}
    >
      <div className="card card-soft">
        <div className="text-muted small px-3 pt-2 d-md-none">
          <i className="bi bi-arrow-left-right me-1"></i>
          {t("timesheet.scroll_hint")}
        </div>
        <table className={`table table-bordered align-middle mb-0 ts-grid ${compact ? "ts-compact" : ""}`} data-testid="timesheet-table">
            <thead className="table-light">
              <tr>
                <th className="ts-name-col">{t("timesheet.th_employee")}</th>
                {dayHeaders.map(h => (
                  <th key={h.d} className={`text-center ts-day-col ${h.isToday ? "ts-today-col" : ""}`}
                      style={{ background: h.isToday ? undefined : (h.calStatus === "weekend" ? "#f1f3f6" : h.calStatus === "holiday" ? "#ffe7e7" : undefined) }}>
                    <div className="ts-day-num">
                      {h.isToday && <i className="bi bi-arrow-down-short ts-today-arrow" aria-hidden="true"></i>}
                      {h.d}
                    </div>
                    <div className="ts-day-wd">{t(`weekdaysShort.${WD_KEYS[h.wd]}`)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map(u => (
                <tr key={u.user_id} data-testid={`ts-row-${u.user_id}`}>
                  <td className="ts-name-col">
                    <div className="fw-semibold small">{u.name}</div>
                    <div className="text-muted ts-role">{t(`roles.${u.role}`)}</div>
                  </td>
                  {dayHeaders.map(h => {
                    const e = timesheet.entries?.[entryKey(u.user_id, h.d)];
                    const editable = canEdit(h.d);
                    const calCls = h.calStatus === "weekend" ? "weekend"
                                 : h.calStatus === "holiday" ? "holiday"
                                 : h.calStatus === "short" ? "short" : "";
                    const status = e?.status;
                    const bg = status ? statusMap[status]?.color : undefined;
                    const fg = status ? "white" : undefined;
                    return (
                      <td key={h.d} className={`ts-cell-wrap ${h.isToday ? "ts-today-cell" : ""}`}>
                        <select
                          className={`timesheet-cell ${calCls} ${!editable ? "disabled" : ""}`}
                          style={{ background: bg || undefined, color: fg || undefined }}
                          value={status || ""}
                          disabled={!editable}
                          onChange={(ev) => onCellChange(u.user_id, h.d, ev.target.value)}
                          data-testid={`ts-cell-${u.user_id}-${h.d}`}
                        >
                          <option value="">·</option>
                          {statuses.map(s => (
                            <option key={s.key} value={s.key}>{s.short}</option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {visibleUsers.length === 0 && (
                <tr><td colSpan={total + 1} className="text-center text-muted py-4">{t("timesheet.no_employees")}</td></tr>
              )}
            </tbody>
          </table>
      </div>
      {!isPrivileged && isManager && (
        <div className="text-muted small mt-2">
          <i className="bi bi-info-circle me-1"></i>
          {t("timesheet.manager_hint")}
        </div>
      )}

      {isPrivileged && firedUsers.length > 0 && (
        <div className="card card-soft mt-3" data-testid="ts-fired-section">
          <div className="card-header bg-light d-flex align-items-center justify-content-between flex-wrap gap-2"
               style={{ cursor: "pointer" }}
               onClick={() => setShowFired(!showFired)}
               data-testid="ts-fired-toggle">
            <div>
              <i className={`bi ${showFired ? "bi-caret-down-fill" : "bi-caret-right-fill"} me-2`}></i>
              <i className="bi bi-archive me-2 text-secondary"></i>
              <strong>{t("timesheet.fired_section")}</strong>
              <span className="badge bg-secondary ms-2">{t("timesheet.fired_count", { n: firedUsers.length })}</span>
            </div>
            <div className="small text-muted d-none d-md-block">{t("timesheet.fired_section_hint")}</div>
          </div>
          {showFired && (
            <div className="ts-scroll">
              <table className={`table table-bordered align-middle mb-0 ts-grid ${compact ? "ts-compact" : ""}`}>
                <thead className="table-light">
                  <tr>
                    <th className="ts-name-col">{t("timesheet.th_employee")}</th>
                    {dayHeaders.map(h => (
                      <th key={h.d} className={`text-center ts-day-col ${h.isToday ? "ts-today-col" : ""}`}
                          style={{ background: h.isToday ? undefined : (h.calStatus === "weekend" ? "#f1f3f6" : h.calStatus === "holiday" ? "#ffe7e7" : undefined) }}>
                        <div className="ts-day-num">{h.d}</div>
                        <div className="ts-day-wd">{t(`weekdaysShort.${WD_KEYS[h.wd]}`)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {firedUsers.map(u => (
                    <tr key={u.user_id} data-testid={`ts-fired-row-${u.user_id}`} style={{ background: "#fafafa" }}>
                      <td className="ts-name-col">
                        <div className="fw-semibold small text-muted" style={{ textDecoration: "line-through" }}>{u.name}</div>
                        <div className="ts-role">
                          <span className="badge bg-secondary me-1">{t("timesheet.fired_badge")}</span>
                          {u.fired_at && (
                            <span className="text-muted" style={{ fontSize: 10 }}>
                              {t("timesheet.fired_at", { date: new Date(u.fired_at).toLocaleDateString() })}
                            </span>
                          )}
                        </div>
                      </td>
                      {dayHeaders.map(h => {
                        const e = timesheet.entries?.[entryKey(u.user_id, h.d)];
                        const calCls = h.calStatus === "weekend" ? "weekend"
                                     : h.calStatus === "holiday" ? "holiday"
                                     : h.calStatus === "short" ? "short" : "";
                        const status = e?.status;
                        const bg = status ? statusMap[status]?.color : undefined;
                        const fg = status ? "white" : undefined;
                        return (
                          <td key={h.d} className={`ts-cell-wrap ${h.isToday ? "ts-today-cell" : ""}`}>
                            <div
                              className={`timesheet-cell disabled ${calCls}`}
                              style={{
                                background: bg || undefined,
                                color: fg || undefined,
                                opacity: 0.85,
                                cursor: "not-allowed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title={status ? statusMap[status]?.label : ""}
                              data-testid={`ts-fired-cell-${u.user_id}-${h.d}`}
                            >
                              {status ? (statusMap[status]?.short || "·") : "·"}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppPage>
  );
}
